import { Link, useRouterState } from '@tanstack/react-router';
import { LayoutGrid, Flame, Play, TrendingUp, Settings, Users, FileText, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMe } from '@/hooks/use-me';

const NAV_LINK_CLASS = 'flex items-center gap-2.5 rounded px-2.5 py-1.5 text-sm font-medium transition-colors relative';

export function Sidebar({ siteId }: { siteId?: string }) {
  const location = useRouterState({ select: (s) => s.location.pathname });
  const { data: me } = useMe();
  const isAdmin = me?.member.role === 'admin';

  const workspaceItems = siteId
    ? [
        { to: `/sites/${siteId}`, label: 'Overview', icon: LayoutGrid, exact: true },
        { to: `/sites/${siteId}/settings`, label: 'Settings', icon: Settings },
      ]
    : [{ to: '/sites', label: 'All sites', icon: LayoutGrid, exact: true }];

  function activeClass(to: string, exact?: boolean) {
    const active = exact ? location === to : location.startsWith(to);
    return cn(
      NAV_LINK_CLASS,
      active ? 'bg-accent-soft text-accent' : 'text-text-muted hover:bg-bg-surface-2 hover:text-text',
    );
  }

  return (
    <nav className="sticky top-14 flex h-[calc(100vh-3.5rem)] flex-col gap-1 border-r border-border bg-bg-base p-3">
      <div className="px-2.5 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-text-subtle">
        Workspace
      </div>
      {/* Heatmap — typed link, route registered in Task 2 */}
      {siteId && (
        <Link
          to="/sites/$siteId/heatmap"
          params={{ siteId }}
          className={activeClass(`/sites/${siteId}/heatmap`)}
        >
          {location.startsWith(`/sites/${siteId}/heatmap`) && (
            <span className="absolute -left-3 top-1.5 bottom-1.5 w-0.5 rounded-r bg-accent" />
          )}
          <Flame className="size-3.5" />
          Heatmap
        </Link>
      )}
      {/* Sessions — typed link, route registered in Task 3 */}
      {siteId && (
        <Link
          to="/sites/$siteId/sessions"
          params={{ siteId }}
          className={activeClass(`/sites/${siteId}/sessions`)}
        >
          {location.startsWith(`/sites/${siteId}/sessions`) && (
            <span className="absolute -left-3 top-1.5 bottom-1.5 w-0.5 rounded-r bg-accent" />
          )}
          <Play className="size-3.5" />
          Sessions
        </Link>
      )}
      {/* Insights — typed link, route registered in Task 5 */}
      {siteId && (
        <Link
          to="/sites/$siteId/insights"
          params={{ siteId }}
          className={activeClass(`/sites/${siteId}/insights`)}
        >
          {location.startsWith(`/sites/${siteId}/insights`) && (
            <span className="absolute -left-3 top-1.5 bottom-1.5 w-0.5 rounded-r bg-accent" />
          )}
          <TrendingUp className="size-3.5" />
          Insights
        </Link>
      )}
      {workspaceItems.map((item) => {
        const active = item.exact ? location === item.to : location.startsWith(item.to);
        return (
          <Link
            key={item.to}
            to={item.to as any}
            className={cn(
              NAV_LINK_CLASS,
              active
                ? 'bg-accent-soft text-accent'
                : 'text-text-muted hover:bg-bg-surface-2 hover:text-text',
            )}
          >
            {active && (
              <span className="absolute -left-3 top-1.5 bottom-1.5 w-0.5 rounded-r bg-accent" />
            )}
            <item.icon className="size-3.5" />
            {item.label}
          </Link>
        );
      })}

      {isAdmin && (
        <>
          <div className="px-2.5 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-text-subtle">
            Admin
          </div>
          <Link to="/admin/members" className="flex items-center gap-2.5 rounded px-2.5 py-1.5 text-sm font-medium text-text-muted hover:bg-bg-surface-2 hover:text-text">
            <Users className="size-3.5" /> Members
          </Link>
          <Link to="/admin/audit" className="flex items-center gap-2.5 rounded px-2.5 py-1.5 text-sm font-medium text-text-muted hover:bg-bg-surface-2 hover:text-text">
            <FileText className="size-3.5" /> Audit log
          </Link>
          <Link to="/admin/system" className="flex items-center gap-2.5 rounded px-2.5 py-1.5 text-sm font-medium text-text-muted hover:bg-bg-surface-2 hover:text-text">
            <Activity className="size-3.5" /> System
          </Link>
        </>
      )}
    </nav>
  );
}
